import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { BannerService, IBanner } from "../services/api/banners/BannerService";
import { BannerPreferencesService } from "../services/api/banners/BannerPreferencesService";

const BANNER_PREF_KEY = "GARANTTI_SHOW_BANNERS";
const BANNER_PREF_SESSION_KEY = "GARANTTI_SHOW_BANNERS_SESSION";

// Cache em memória (por aba/sessão SPA). Evita re-fetch ao sair/voltar do dashboard.
let bannerCache: { banners: IBanner[] } | null = null;

interface BannerContextData {
    banners: IBanner[];
    showBanners: boolean;
    toggleShowBanners: () => void;
    refreshBanners: () => void;
    isLoading: boolean;
}

const BannerContext = createContext<BannerContextData>({} as BannerContextData);

export const useBannerContext = () => useContext(BannerContext);

export const BannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [banners, setBanners] = useState<IBanner[]>(() => bannerCache?.banners ?? []);
    const [showBanners, setShowBanners] = useState<boolean>(() => {
        const sessionStored = sessionStorage.getItem(BANNER_PREF_SESSION_KEY);
        if (sessionStored !== null) return JSON.parse(sessionStored);

        const stored = localStorage.getItem(BANNER_PREF_KEY);
        return stored ? JSON.parse(stored) : false;
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingPreference, setIsLoadingPreference] = useState(false);
    const [isPreferenceLoaded, setIsPreferenceLoaded] = useState<boolean>(() => {
        return sessionStorage.getItem(BANNER_PREF_SESSION_KEY) !== null;
    });

    // Preferência global: buscar 1x por sessão/aba (cache via sessionStorage)
    useEffect(() => {
        if (isPreferenceLoaded) return;

        const sessionStored = sessionStorage.getItem(BANNER_PREF_SESSION_KEY);
        if (sessionStored !== null) {
            setShowBanners(JSON.parse(sessionStored));
            setIsPreferenceLoaded(true);
            return;
        }

        let isMounted = true;
        setIsLoadingPreference(true);
        BannerPreferencesService.getDashboardVisibility()
            .then((result) => {
                if (!isMounted) return;

                if (result instanceof Error) {
                    // Fail-safe: endpoint ainda não existe / erro -> usa localStorage (legado) ou false.
                    const stored = localStorage.getItem(BANNER_PREF_KEY);
                    const fallback = stored ? JSON.parse(stored) : false;

                    sessionStorage.setItem(BANNER_PREF_SESSION_KEY, JSON.stringify(fallback));
                    setShowBanners(fallback);
                    setIsPreferenceLoaded(true);
                    return;
                }

                // Mantém compatibilidade: salva também em localStorage (não é a fonte de verdade)
                localStorage.setItem(BANNER_PREF_KEY, JSON.stringify(result.showBanners));
                sessionStorage.setItem(BANNER_PREF_SESSION_KEY, JSON.stringify(result.showBanners));
                setShowBanners(result.showBanners);
                setIsPreferenceLoaded(true);
            })
            .finally(() => {
                if (!isMounted) return;
                setIsLoadingPreference(false);
            });

        return () => {
            isMounted = false;
        };
    }, [isPreferenceLoaded]);

    const fetchBanners = useCallback(async (force = false) => {
        if (!showBanners) {
            setBanners([]);
            return;
        }

        if (!force && bannerCache?.banners?.length) {
            setBanners(bannerCache.banners);
            return;
        }

        setIsLoading(true);
        try {
            // Fetch all banners (or a reasonable limit for the carousel)
            // We assume the carousel shows active banners sorted by order.
            // The API supports filtering, but for now we fetch page 1 with a larger limit if needed,
            // or we can rely on the standard limit.
            // Ideally, we'd have an endpoint for "active banners for dashboard".
            // For now, we fetch standard list and filter active ones client-side if API doesn't filter.
            // Based on BannerService, we can filter by title, but not explicitly 'active' in getAll params shown.
            // We will fetch and filter client side for this implementation.

            const result = await BannerService.getAll(1, ""); // Fetch page 1
            if (result instanceof Error) {
                console.error(result.message);
            } else {
                // Filter active and sort by order
                const activeBanners = result.data
                    .filter(b => b.active)
                    .sort((a, b) => a.order - b.order);
                setBanners(activeBanners);

                bannerCache = { banners: activeBanners };
            }
        } catch (error) {
            console.error("Failed to fetch banners", error);
        } finally {
            setIsLoading(false);
        }
    }, [showBanners]);

    useEffect(() => {
        // Evita chamar listar banners antes de saber a preferência (1º load da sessão)
        if (!isPreferenceLoaded) return;
        fetchBanners();
    }, [fetchBanners, isPreferenceLoaded]);

    const toggleShowBanners = useCallback(() => {
        setShowBanners((prev: boolean) => {
            const newValue = !prev;
            localStorage.setItem(BANNER_PREF_KEY, JSON.stringify(newValue));
            sessionStorage.setItem(BANNER_PREF_SESSION_KEY, JSON.stringify(newValue));
            setIsPreferenceLoaded(true);

            // Atualiza backend. Mantém otimista, mas reverte em caso de falha.
            BannerPreferencesService.setDashboardVisibility(newValue).then((result) => {
                if (result instanceof Error) {
                    localStorage.setItem(BANNER_PREF_KEY, JSON.stringify(prev));
                    sessionStorage.setItem(BANNER_PREF_SESSION_KEY, JSON.stringify(prev));
                    setShowBanners(prev);
                }
            });

            return newValue;
        });
    }, []);

    const refreshBanners = useCallback(() => {
        fetchBanners(true);
    }, [fetchBanners]);

    return (
        <BannerContext.Provider value={{ banners, showBanners, toggleShowBanners, refreshBanners, isLoading: isLoading || isLoadingPreference }}>
            {children}
        </BannerContext.Provider>
    );
};
